import { apiV1 } from "api-v2";
import { ProcuraconContextType } from "newui/types/ContextType.type";
import { Conversation, CreateConversation } from "./types/Conversation.type";
import { ApiResponse } from "types/common.types";
import { Message } from "./types/Message.type";
import { UploadedFile } from "newui/components/FileUpload";

export const createConversation = async (
	creationData: CreateConversation
): Promise<Conversation | null> => {
	const result = await apiV1.post<ApiResponse<Conversation>>(
		`/conversations`,
		creationData
	);

	return result?.data?.data;
};

/**
 * Fetches all conversations for the given RFx - this is a client-only api call (suppliers will call with conversation context)
 * @param rfxId The Id of the RFx
 * @returns all current Conversations
 */
export const fetchConversationsForClientRfx = async (
	rfxId: number
): Promise<Conversation[]> => {
	const response = await apiV1.get<ApiResponse<Conversation[]>>(
		`/conversations/rfx/${rfxId}`,
		{
			params: { context_id: ProcuraconContextType.RFX_PRE_SUBMISSION },
		}
	);

	if (response.data) {
		return response.data?.data;
	}

	return [];
};

export const fetchConversationsByContextId = async (
	contextId: number,
	contextType: ProcuraconContextType
): Promise<Conversation[]> => {
	const response = await apiV1.get<ApiResponse<Conversation[]>>(
		`/conversations/${contextId}`,
		{
			params: { contextTypeId: contextType },
		}
	);

	if (response.data) {
		return response.data?.data;
	}

	return [];
};

/**
 * Fetches all messages for the given Conversation
 * @param conversationId The Id of the conversation
 * @returns all current messages
 */
export const fetchMessagesForConversation = async (
	conversationId: number
): Promise<Message[]> => {
	const response = await apiV1.get<ApiResponse<Message[]>>(
		`/conversations/${conversationId}/messages`
	);

	if (response.data) {
		return response.data?.data.toReversed();
	}

	return [];
};

export const createMessage = async (
	conversationId: number,
	body: string,
	files: UploadedFile[]
): Promise<Message | null> => {
	const result = await apiV1.post<ApiResponse<Message>>(
		`/conversations/${conversationId}/messages`,
		{
			body,
			files: files?.map((file) => file.id),
		}
	);

	return result?.data?.data;
};

export const markConversationRead = async (
	conversationId: number
): Promise<boolean> => {
	const result = await apiV1.patch<ApiResponse<Conversation>>(
		`/conversations/${conversationId}/messages/read`
	);

	return result?.status === 200;
};
